import { useCallback, type ReactElement } from 'react';
import {
  MermaidRuntime,
  preloadMermaidRuntime,
} from '../../../ui/components/diagram/MermaidRuntime';
import { DECK_MERMAID_THEME } from './deck-theme';
import { DECK } from './deck-theme';
import { requestSlideRefit } from './SlideFrame';
import './slides.css';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Mermaid serializes flat colours. Add the Festival gradients after the SVG exists.
 *
 * No grain: the `feTurbulence`/soft-light filter the hot and machine nodes used to carry is gone
 * (2026-08-13) — it read as noise over the "Code"/"Computer" nodes when projected. The hot
 * `.deck-hot-surface` cards keep their texture; the diagram nodes are flat gradients. */
export function decorateDeckDiagram(svg: SVGSVGElement): void {
  const suffix = svg.id.replace(/[^a-zA-Z0-9_-]/g, '') || 'deck';
  const defs = document.createElementNS(SVG_NS, 'defs');
  defs.innerHTML = `
    <linearGradient id="lava-${suffix}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="${DECK.secondary}"/><stop offset="1" stop-color="${DECK.pinkLavaHalf}"/>
    </linearGradient>
    <linearGradient id="ocean-${suffix}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="${DECK.ocean}"/><stop offset="1" stop-color="${DECK.oceanLightHalf}"/>
    </linearGradient>`;
  svg.prepend(defs);
  for (const shape of svg.querySelectorAll<SVGElement>('.hot rect, .hot polygon, .hot path')) {
    shape.style.fill = `url(#lava-${suffix})`;
    shape.style.stroke = 'none';
  }
  for (const shape of svg.querySelectorAll<SVGElement>('.machine rect, .machine polygon, .machine path')) {
    shape.style.fill = `url(#ocean-${suffix})`;
    shape.style.stroke = 'none';
  }
}

/** Starts Mermaid while `/demo` is booting, before a graph becomes audience-visible. */
export const preloadDeckDiagrams = preloadMermaidRuntime;

export function MermaidDiagram({
  source,
  revealTestId,
}: {
  source: string;
  revealTestId?: string;
}): ReactElement {
  const decorateSvg = useCallback(decorateDeckDiagram, []);
  return (
    <MermaidRuntime
      source={source}
      ariaLabel="Presentation diagram"
      testId={revealTestId}
      className="demo-mermaid"
      themeVariables={DECK_MERMAID_THEME}
      decorateSvg={decorateSvg}
      onRendered={requestSlideRefit}
    />
  );
}
